import DashboardTableShare from "@/components/DashboardTableShare/DashboardTableShare";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import Table, { Column } from "@/components/Table/Table";
import useTableState from "@/components/Table/hooks/useTableState";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import useTranslation from "@/hooks/useTranslation";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { formatAmountWithUnit, formatSupplier } from "@/pages/dashboard/utils/formatters";
import { DownloadOutlined } from "@ant-design/icons";
import { Segmented } from "antd";
import { useEffect, useState } from "react";
import Widget from "../../components/Widget/Widget";
import {
  IMaterialImpactPerMaterial,
  MaterialGroupBy,
  useMaterialImpactPerMaterial,
  useMaterialImpactPerMaterialExport,
} from "../../hooks/useMaterialImpact";
import { ImpactMetric } from "../../utils/types";

export default function MaterialImpactPerMaterial({ metric }: { metric: ImpactMetric }) {
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination, setSort } =
    useTableState({
      defaultPagination: { page: 1, pageSize: 10 },
      defaultSort: {
        key: metric.key,
        order: "descend",
      },
    });
  const { startDate, endDate } = useDateRange();
  const [groupBy, setGroupBy] = useState<MaterialGroupBy>("name");

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);
  const translation = useTranslation();

  const columns: Column<IMaterialImpactPerMaterial>[] = [
    {
      label: "Material",
      key: "name",
      sortable: true,
      maxWidth: "2fr",
      searchable: true,
      renderer: (record) =>
        groupBy === "id" ? (
          <CrossReference to={`/materials/${record.id}`}>{record.name}</CrossReference>
        ) : (
          record.name
        ),
    },
    {
      label: "Supplier",
      key: "suppliers",
      searchable: true,
      maxWidth: "2fr",
      renderer: (row) =>
        row.suppliers.length === 1
          ? formatSupplier(row.suppliers[0].name, row.suppliers[0].address)
          : row.suppliers.length + " suppliers",
    },
    {
      label: "Total amount",
      key: "quantity",
      renderer: (record) => {
        return `${formatAmountWithUnit(record.quantity, record.unit, 2)}`;
      },
    },
    {
      label: "Share",
      key: "share",
      maxWidth: "1fr",
      renderer: (record) => {
        return (
          <DashboardTableShare
            total={record.impact[metric.key]}
            value={data?.total_material_impact[metric.key]}
          />
        );
      },
    },
    {
      label: metric.label,
      key: metric.key,
      sortable: true,
      renderer: (record) => metric.formatter(record.impact[metric.key]),
    },
  ];

  const { data, isLoading, isFetching } = useMaterialImpactPerMaterial({
    pagination,
    filter,
    search,
    sort,
    groupBy,
    metric: metric.key,
  });

  const exportMutation = useMaterialImpactPerMaterialExport({
    pagination,
    filter,
    search,
    sort,
    groupBy,
    metric: metric.key,
  });

  useEffect(() => {
    setSort({ key: metric.key, order: "descend" });
  }, [metric]);

  return (
    <Widget
      title={"Material impact per material"}
      tooltip={translation["dashboard"]["material"]["per_material"]}
      topRight={
        <>
          <Segmented
            value={groupBy}
            onChange={setGroupBy}
            options={[
              { label: "Unique", value: "id" },
              { label: "Grouped", value: "name" },
            ]}
          />
          <FileDownloader
            fileUrlFn={() => exportMutation.mutateAsync()}
            buttonText={<DownloadOutlined />}
            fileOutputName="material-impact-per-material.xlsx"
            addTimestampAndCompany={true}
          />
        </>
      }
    >
      <Table
        hideRaster={true}
        type={"grey"}
        filter={filter}
        sort={sort}
        search={search}
        columns={columns}
        data={data?.data}
        loading={isLoading || isFetching}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        pagination={pagination}
        totalRows={data?.total_rows}
        rowKey={"key"}
      />
    </Widget>
  );
}
